import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import RunsHistogram from 'features/agent-analytics/components/RunsHistogram';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import TracesViewTabs from 'features/agent-analytics/components/TracesViewTabs';
import AgentMultiSelect from 'features/agent-analytics/components/AgentMultiSelect';
import { Filter as FilterIcon } from 'lucide-react';
import EvalFilterSection, { withEvalFilter, evalConditionCount, readEvalLines, writeEvalLines } from 'features/agent-analytics/components/EvalFilterSection';
import { QUERY_PANEL_CLASS } from 'shared/components/filters/queryBuilderChrome';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import SemanticSearchBar from 'features/agent-analytics/components/SemanticSearchBar';
import TableFooter from 'shared/components/tables/TableFooter';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { listConversations } from 'features/agent-analytics/api/conversationsApi';
import { turnPreview } from 'features/agent-analytics/utils/conversationTurns';
import {
  formatElapsed, formatCost, formatRelativeTime, formatDate, truncate, ScorePill,
} from 'features/agent-analytics/components/runCells';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import AnnotationBadge from 'features/agent-analytics/annotations/AnnotationBadge';
import { useAnnotations } from 'features/agent-analytics/annotations';

const PAGE_SIZE = 25;

const COLUMNS = [
  { key: 'conversation', label: 'Conversation' },
  { key: 'preview', label: 'Opening message' },
  { key: 'runs', label: 'Runs' },
  { key: 'agents', label: 'Agents' },
  { key: 'user', label: 'User' },
  { key: 'elapsed', label: 'Elapsed' },
  { key: 'cost', label: 'Cost' },
  { key: 'sat', label: 'Sat' },
  { key: 'last', label: 'Last activity' },
];

/**
 * Conversations tab of the Traces page — one row per conversation_id, opening a
 * chat-style transcript drawer.
 *
 * Runs and spans answer "what did this execution do"; a conversation answers
 * "what was this person actually trying to get done", which is the question a
 * support or product person arrives with.
 */
export default function ConversationsPage() {
  useBreadcrumb([{ label: 'Traces' }]);
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = typeof window === 'undefined' ? '' : localStorage.getItem('selectedTeam') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [openIndex, setOpenIndex] = useState(-1);
  const [drawerTab, setDrawerTab] = useState(() => (
    searchParams.get('tab') === 'annotations' ? 'annotations' : 'transcript'
  ));
  const [deepConversationId, setDeepConversationId] = useState(() => searchParams.get('conversation_id') || '');
  const [deepTab] = useState(() => searchParams.get('tab') || '');
  const { prefetchCounts } = useAnnotations();

  const [timePreset, setTimePreset] = useState(searchParams.get('preset') || DEFAULT_LIST_PRESET);
  const timeRange = useMemo(() => presetToISORange(timePreset), [timePreset]);
  // Empty = every agent. Same agent filter shape the Runs tab sends.
  const [agentNames, setAgentNames] = useState(() => (searchParams.get('agents') || '').split(',').filter(Boolean));
  const [query, setQuery] = useState(searchParams.get('q') || '');
  // EVALS: conversations with a run the evals said something about (URL `evals`).
  const [evalLines, setEvalLines] = useState(() => readEvalLines(searchParams));
  const [evalsShown, setEvalsShown] = useState(() => readEvalLines(searchParams).length > 0);
  const evalCount = evalConditionCount(evalLines);
  const modularFilters = useMemo(
    () => withEvalFilter(agentNames.length ? [{ field: 'agent_name', operator: 'is', value: agentNames, category: 'agents' }] : [], evalLines),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agentNames, JSON.stringify(evalLines)],
  );

  const load = useCallback(async (offset = 0) => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listConversations({ teamId, timeRange, modularFilters, q: query.trim(), limit: PAGE_SIZE, offset });
      setRows((prev) => (offset === 0 ? data.conversations : [...prev, ...data.conversations]));
      setHasMore(data.hasMore);
      if (offset === 0 || data.total != null) setTotal(data.total);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  }, [teamId, timeRange, modularFilters, query]);

  useEffect(() => { load(0); }, [load]);

  // Agents and search live in the URL, like the Runs tab's filters.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (agentNames.length) next.set('agents', agentNames.join(',')); else next.delete('agents');
    if (query.trim()) next.set('q', query.trim()); else next.delete('q');
    writeEvalLines(next, evalLines);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentNames, query, evalLines]);

  useEffect(() => {
    if (!deepConversationId) return;
    if (deepTab === 'annotations') setDrawerTab('annotations');
    const idx = rows.findIndex((r) => String(r.conversation_id) === String(deepConversationId));
    if (idx >= 0) setOpenIndex(idx);
  }, [deepConversationId, deepTab, rows]);

  useEffect(() => {
    prefetchCounts({ conversationIds: rows.map((r) => r.conversation_id).filter(Boolean) });
  }, [rows, prefetchCounts]);

  const onTimeFilterChange = (filter) => {
    const preset = filter?.type === 'preset' ? filter.preset : 'custom';
    setTimePreset(preset);
    const next = new URLSearchParams(searchParams);
    next.set('preset', preset);
    setSearchParams(next, { replace: true });
  };

  const open = openIndex >= 0 ? rows[openIndex] : null;
  const drawerConversationId = open?.conversation_id || (openIndex < 0 ? deepConversationId : '');

  const cell = (row, key) => {
    switch (key) {
      case 'conversation':
        return (
          <span className="inline-flex max-w-full items-center gap-1.5">
            <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={String(row.conversation_id)}>
              {truncate(String(row.conversation_id), 16)}
            </span>
            <AnnotationBadge
              conversationId={row.conversation_id}
              onClick={(e) => {
                e.stopPropagation();
                setDrawerTab('annotations');
                setOpenIndex(rows.findIndex((r) => r.conversation_id === row.conversation_id));
              }}
            />
          </span>
        );
      case 'preview': {
        const text = turnPreview(row.first_input, 70);
        return text
          ? <span className="text-[color:var(--text-primary)]" title={text}>{text}</span>
          : <span className="text-[color:var(--text-muted)]">—</span>;
      }
      case 'runs':
        return (
          <span className="inline-flex items-center gap-1.5">
            <span className="tabular-nums">{row.run_count}</span>
            {row.error_count > 0 ? (
              <span className="tabular-nums text-[color:var(--feedback-error)]" title={`${row.error_count} failed`}>
                ({row.error_count} err)
              </span>
            ) : null}
          </span>
        );
      case 'agents': {
        const list = row.agents || [];
        if (!list.length) return <span className="text-[color:var(--text-muted)]">—</span>;
        return (
          <span title={list.join(', ')}>
            {list[0]}{list.length > 1 ? ` +${list.length - 1}` : ''}
          </span>
        );
      }
      case 'user':
        return row.distinct_id
          ? <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={row.distinct_id}>{truncate(row.distinct_id, 18)}</span>
          : <span className="text-[color:var(--text-muted)]">—</span>;
      case 'elapsed':
        return <span className="tabular-nums">{row.span_ms != null ? formatElapsed(row.span_ms) : '—'}</span>;
      case 'cost':
        return <span className="tabular-nums">{row.total_cost ? formatCost(row.total_cost) : '—'}</span>;
      case 'sat':
        return <ScorePill value={row.satisfaction_score} kind="sat" />;
      case 'last':
        return (
          <span className="tabular-nums text-[color:var(--text-secondary)]" title={formatDate(row.last_run_at)}>
            {formatRelativeTime(row.last_run_at)}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <main className="flex flex-col bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <TracesViewTabs value="conversations" />

      <div className="flex flex-col gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <EventsTimeFilter selectedPreset={timePreset} onTimeFilterChange={onTimeFilterChange} />
          <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="flex items-center gap-1.5 px-3 text-[12px]">
              <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                {(total ?? rows.length).toLocaleString()}
              </span>
              <span className="text-[color:var(--text-muted)]">conversations</span>
            </div>
          </div>
          <SemanticSearchBar value={query} onChange={setQuery} placeholder="Search conversation or user…" width={240} />
          <AgentMultiSelect teamId={teamId} value={agentNames} onChange={setAgentNames} />
          <button type="button" onClick={() => setEvalsShown((v) => !v)} className={activityToolbarButtonClass} aria-expanded={evalsShown}>
            <FilterIcon size={14} color="var(--text-muted)" />
            Evals
            {evalCount > 0 ? (
              <span className="rounded-full bg-surface-hover px-1.5 text-[10px] font-semibold text-[color:var(--text-primary)]">{evalCount}</span>
            ) : null}
          </button>
        </div>
        {evalsShown ? (
          <div className={QUERY_PANEL_CLASS}>
            <EvalFilterSection teamId={teamId} value={evalLines} onChange={setEvalLines} />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col gap-3 px-4 pb-8 pt-3">
        <RunsHistogram teamId={teamId} timeRange={timeRange} modularFilters={modularFilters} />

        {error ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : null}

        {loading && rows.length === 0 ? (
          <TableSkeleton columns={COLUMNS.map((c) => c.label)} rows={12} />
        ) : rows.length === 0 ? (
          <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="px-6 py-8 text-center text-[12px] text-[color:var(--text-secondary)]">
              {query.trim() || agentNames.length
                ? 'No conversations match these filters.'
                : <>No conversations in this period. Runs need a <span className="font-mono">conversation_id</span> to group.</>}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className={tableHeaderCellClass}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={row.conversation_id}
                      onClick={() => {
                        // A row opens on its transcript; only the comment badge opens Comments.
                        setDrawerTab('transcript');
                        setOpenIndex(i);
                      }}
                      className={`${tableRowClass} ${openIndex === i ? 'bg-surface-hover' : ''}`}
                    >
                      {COLUMNS.map((c) => (
                        <td key={c.key} className={tableDataCellClass}>
                          <span className="block max-w-[320px] truncate">{cell(row, c.key)}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <TableFooter shown={rows.length} total={total} hasMore={hasMore} loading={loading} onLoadMore={() => load(rows.length)} />
      </div>

      {drawerConversationId ? (
        <ConversationDetailDrawer
          conversationId={drawerConversationId}
          teamId={teamId}
          // One thread can hold several sittings; bound the drawer to this row's.
          from={open?.first_run_at}
          to={open?.last_run_at}
          initialTab={drawerTab}
          onClose={() => {
            setOpenIndex(-1);
            setDeepConversationId('');
          }}
          onPrev={() => setOpenIndex((i) => Math.max(0, i - 1))}
          onNext={() => setOpenIndex((i) => Math.min(rows.length - 1, i + 1))}
          hasPrev={openIndex > 0}
          hasNext={openIndex >= 0 && openIndex < rows.length - 1}
        />
      ) : null}
    </main>
  );
}
